import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Globals } from '@smarttask-common/src/lib/services/globals';
import { Deferred } from '@smarttask-common/src/lib/services/AngularSpecific/Deferred';
import { SharedService } from '@smarttask-app/src/app/services/AngularSpecific/shared.service';
import { SharedEventNameEnum } from '@smarttask-common/src/lib/models/AngularSpecific/SharedEventNameEnum';
import { AuthenticationModel } from '@smarttask-common/src/lib/models/Identity/Authentication/AuthenticationModel';
import { SharedEventModel } from '@smarttask-common/src/lib/models/AngularSpecific/SharedEventModel';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { MiscService } from '@smarttask-common/src/lib/services/General/misc.service';
import { jwtDecode } from 'jwt-decode';
import { InitializeTypeEnum } from '@smarttask-common/src/lib/models/Initialize/InitializeTypeEnum';
import { AuthServerResponseModel } from '@smarttask-common/src/lib/models/Identity/Authentication/AuthServerResponseModel';
import mixpanel from 'mixpanel-browser';
import { TrackEventEnum } from '@smarttask-common/src/lib/models/Internal/Analytics/TrackEventEnum';
import { DateTime } from 'luxon';
import { generateUUID } from '@smarttask-common/src/lib/services/General/utils';
import { CookieService } from 'ngx-cookie-service';
import { OneSignalService } from '@smarttask-app/src/app/services/Notification/oneSignal.service';
declare let Intercom;
declare let cioanalytics;

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    logging = false;

    constructor(
        private http: HttpClient,
        private globals: Globals,
        private sharedService: SharedService,
        private miscService: MiscService,
        private cookieService: CookieService,
        private oneSignalService: OneSignalService,
    ) {
        this.listenForRefreshTokenExpired();
        this.listenForLogout();
        this.listenForGoToLoginPage();
    }

    triggerAuthorizationFlow = async () => {
        let url = new URL(window.location.href);
        url.hash = '';
        let state = url.toString();

        let redirect_url = this.globals.app_base_url + 'authorization';

        let code_verifier = generateUUID() + 'asfftrscadvgdrta'; //Has to be 43+ in length
        //console.log("Code Verifier - " + code_verifier);
        this.cookieService.set(this.globals.cookie_names.code_verifier, code_verifier, {
            path: '/',
            domain: this.globals.cookie_domain,
            expires: DateTime.now().plus({ hour: 1 }).toJSDate()
        });
        let code_challenge = await this.miscService.sha256(code_verifier);
        code_challenge = this.miscService.hexToBase64Url(code_challenge);
        let code_challenge_method = 'S256';

        window.location.href =
            `${this.globals.identity_base_url}${this.globals.auth.tenant_id}/${this.globals.auth.signup_signin_policy}/oauth2/v2.0/authorize` +
            `?client_id=${encodeURIComponent(this.globals.auth.client_id)}` +
            '&response_type=code' +
            `&response_mode=query` +
            `&scope=${encodeURIComponent(this.globals.auth.scope)}` +
            `&redirect_uri=${encodeURIComponent(redirect_url)}` +
            `&state=${encodeURIComponent(state)}` +
            '&nonce=abc' +
            `&code_challenge=${encodeURIComponent(code_challenge)}` +
            `&code_challenge_method=${code_challenge_method}`;
    };

    getAccessTokenFromAuthorizationCode = (code: string, state: string) => {
        if (this.logging) {
            return undefined;
        }
        this.logging = true;
        let code_verifier = this.cookieService.get(this.globals.cookie_names.code_verifier);
        //console.log(`Code Verifier - ${code_verifier}`);
        let url = `${this.globals.identity_base_url}${this.globals.auth.tenant_id}/${this.globals.auth.signup_signin_policy}/oauth2/v2.0/token`;
        let to_post =
            `grant_type=authorization_code` +
            `&client_id=${this.globals.auth.client_id}` +
            `&scope=${this.globals.auth.scope}` +
            `&code=${code}` +
            `&code_verifier=${code_verifier}` +
            `&redirect_uri=${this.globals.app_base_url}`;

        //console.log(JSON.stringify(to_post, null, 4))
        return this.http
            .post(url, to_post, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            })
            .toPromise()
            .then(
                (data: AuthServerResponseModel) => {
                    this.globals.auth_data = this.getDecodedToken(data);
                    // console.log(JSON.stringify(this.globals.auth_data, null, 4))
                    //Only add access_token and refresh_token in cookie. As otherwise the cookie size limit is an issue
                    let cookie_obj = {
                        access_token: data.access_token,
                        refresh_token: data.refresh_token,
                    };
                    //Add cookies
                    this.cookieService.set(this.globals.cookie_names.auth, JSON.stringify(cookie_obj), {
                        expires: DateTime.now().plus({ year: 1 }).toJSDate(),
                        path: '/',
                        domain: this.globals.cookie_domain,
                    });
                    this.cookieService.set(this.globals.cookie_names.user_id, this.globals.auth_data.user_id.toString(), {
                        expires: DateTime.now().plus({ year: 1 }).toJSDate(),
                        path: '/',
                        domain: this.globals.cookie_domain,
                    });
                    //Inform other services about login
                    let event = new SharedEventModel();
                    event.name = SharedEventNameEnum.login;
                    this.sharedService.broadcast(event);

                    if (state.indexOf('http') > -1) {
                        window.location.href = decodeURIComponent(state);
                    } else {
                        window.location.href = this.globals.app_base_url;
                    }
                },
                (response: HttpErrorResponse) => {
                    console.error('Getting token from auth_code was not successful');
                    console.log(JSON.stringify(response, null, 4));
                    this.logout('authorization code failed - log out');
                    return Promise.reject(response);
                },
            )
            .finally(() => {
                this.logging = false;
            });
    };

    //Used for routing
    //Returns promise to check whether the current user is authenticated
    authenticate = () => {
        if (this.globals.auth_data != undefined) {
            // Resolve the promise successfully
            return Promise.resolve();
        } else {
            console.log('Auth data not present');
            // This code runs after the authentication promise has been rejected.
            let cookie_value = this.cookieService.get(this.globals.cookie_names.auth);
            let cookie_data: AuthenticationModel = undefined;
            if(cookie_value != undefined && cookie_value.length > 0){
                cookie_data = JSON.parse(cookie_value);
            }
            if (cookie_data && cookie_data.access_token != undefined && cookie_data.access_token.length > 0 && cookie_data.refresh_token != undefined && cookie_data.refresh_token.length > 0) {
                this.globals.auth_data = this.decodeAccessToken(cookie_data.access_token);
                return Promise.resolve();
            } else {
                // Go to the log-in page
                this.triggerAuthorizationFlow();
                // Reject the authentication promise to prevent the state from loading
                return Promise.reject('Authentication Check Failed');
            }
        }
    };

    refreshToken = (): Observable<AuthServerResponseModel> => {
        let refresh_token = '';
        let cookie_value = this.cookieService.get(this.globals.cookie_names.auth);
        let cookie_data: AuthenticationModel = undefined;
        if(cookie_value != undefined || cookie_value.length > 0){
            cookie_data = JSON.parse(cookie_value);
        }
        if (cookie_data) {
            refresh_token = cookie_data.refresh_token;
            // console.log(`Refresh token - ${refresh_token}`);
        }
        if (refresh_token == undefined || refresh_token.length <= 0) {
            return throwError(() => new Error('RefreshToken not present'));
        }

        let url = `${this.globals.identity_base_url}${this.globals.auth.tenant_id}/${this.globals.auth.signup_signin_policy}/oauth2/v2.0/token`;
        let to_post = `grant_type=refresh_token&client_id=${encodeURIComponent(
            this.globals.auth.client_id,
        )}&refresh_token=${encodeURIComponent(refresh_token)}&scope=${encodeURIComponent(this.globals.auth.scope)}`;

        return this.http
            .post(url, to_post, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            })
            .pipe(
                map((data: AuthServerResponseModel) => {
                    this.globals.auth_data = this.getDecodedToken(data);

                    //only save access and refresh_token
                    let cookie_obj = {
                        access_token: data.access_token,
                        refresh_token: data.refresh_token,
                    };
                    this.cookieService.set(this.globals.cookie_names.auth, JSON.stringify(cookie_obj), {
                        expires: DateTime.now().plus({ year: 1 }).toJSDate(),
                        path: '/',
                        domain: this.globals.cookie_domain,
                    });
                    this.cookieService.set(this.globals.cookie_names.user_id, this.globals.auth_data.user_id.toString(), {
                        expires: DateTime.now().plus({ year: 1 }).toJSDate(),
                        path: '/',
                        domain: this.globals.cookie_domain,
                    });
                    return data;
                }),
            );
    };

    initializeImpersonatedLogin = (access_token: string, state: string) => {
        this.globals.auth_data = this.decodeAccessToken(access_token);
        // console.log(JSON.stringify(this.globals.auth_data, null, 4))
        //Only add access_token and refresh_token in cookie. As otherwise the cookie size limit is an issue
        let cookie_obj = {
            access_token: access_token,
        };
        //Add cookies
        this.cookieService.set(this.globals.cookie_names.auth, JSON.stringify(cookie_obj), {
            expires: DateTime.now().plus({ year: 1 }).toJSDate(),
            path: '/',
            domain: this.globals.cookie_domain,
        });
        this.cookieService.set(this.globals.cookie_names.user_id, this.globals.auth_data.user_id.toString(), {
            expires: DateTime.now().plus({ year: 1 }).toJSDate(),
            path: '/',
            domain: this.globals.cookie_domain,
        });
        //Inform other services about login
        let event = new SharedEventModel();
        event.name = SharedEventNameEnum.login;
        this.sharedService.broadcast(event);

        window.location.href = this.globals.app_base_url;
    };

    getEmbeddedPasswordResetUrl = () => {
        return `${this.globals.identity_base_url}${this.globals.auth.tenant_id}/${this.globals.auth.password_reset_policy}/oauth2/v2.0/authorize?client_id=${this.globals.auth.client_id}&nonce=defaultNonce&redirect_uri=${encodeURIComponent(this.globals.app_base_url)}&scope=openid&response_type=id_token&product=smarttask`;
    };

    //Called in intialization and during login
    fillAuthData = (): Promise<any> => {
        let deferred = new Deferred<void>();
        let cookie_value = this.cookieService.get(this.globals.cookie_names.auth);
        let auth_server_data: AuthenticationModel = undefined;
        if(cookie_value != undefined && cookie_value.length > 0){
            auth_server_data = JSON.parse(cookie_value);
        }
        if (auth_server_data == undefined) {
            if (mixpanel != undefined && this.globals.mixpanel_loaded) {
                mixpanel.track_with_groups(TrackEventEnum.user_auth_failed, {}, { organization_id: [] });
            }
            deferred.reject(InitializeTypeEnum.auth_data_not_present);
        } else {
            this.globals.auth_data = this.getDecodedToken(auth_server_data);
            if (this.globals.auth_data && this.globals.auth_data.user_id != undefined) {
                if (typeof Intercom != 'undefined' && Intercom != undefined) {
                    //Update Intercom info
                    Intercom('update', {
                        user_id: this.globals.auth_data.user_id,
                        email: this.globals.auth_data.email,
                        name: this.globals.auth_data.display_name,
                        phone: this.globals.auth_data.mobile,
                    });
                }

                if (mixpanel != undefined && this.globals.mixpanel_loaded) {
                    mixpanel.identify(this.globals.auth_data.user_id.toString());
                    mixpanel.track_with_groups(TrackEventEnum.user_auth_success, {}, { organization_id: [] });
                    mixpanel.people.set({
                        user_id: this.globals.auth_data.user_id,
                        $name: this.globals.auth_data.display_name,
                        $email: this.globals.auth_data.email,
                        $phone: this.globals.auth_data.mobile,
                    });
                }

                if(typeof cioanalytics != 'undefined' && cioanalytics != undefined){
                    cioanalytics.identify(this.globals.auth_data.user_id, {
                        email: this.globals.auth_data.email,
                        name: this.globals.auth_data.display_name,
                        phone: this.globals.auth_data.mobile
                    });
                }

                deferred.resolve();
            } else {
                this.globals.auth_data = undefined;
                if (mixpanel != undefined && this.globals.mixpanel_loaded) {
                    mixpanel.track_with_groups(TrackEventEnum.user_auth_failed, {}, { organization_id: [] });
                }
                deferred.reject(InitializeTypeEnum.auth_data_not_present);
            }
        }
        return deferred.promise;
    };

    decodeAccessToken = (access_token: string): AuthenticationModel => {
        let decoded_data = <any>jwtDecode(access_token);
        let to_ret: AuthenticationModel = {
            display_name: decoded_data.display_name,
            email: decoded_data.email,
            email_verified: decoded_data.email_verified,
            sub: decoded_data.sub,
            mobile: decoded_data.mobile,
            user_id: parseInt(decoded_data.links_user_id),
            can_impersonate: decoded_data.can_impersonate,
            access_token: access_token,
            refresh_token: undefined,
        };
        return to_ret;
    };
    getDecodedToken = (data: AuthServerResponseModel): AuthenticationModel => {
        let decoded_data = <any>jwtDecode(data.access_token);
        let to_ret: AuthenticationModel = {
            display_name: decoded_data.display_name,
            email: decoded_data.email,
            email_verified: decoded_data.email_verified,
            sub: decoded_data.sub,
            mobile: decoded_data.mobile,
            user_id: parseInt(decoded_data.custom_user_id),
            can_impersonate: decoded_data.can_impersonate,
            access_token: data.access_token,
            refresh_token: data.refresh_token,
        };
        return to_ret;
    };

    sso = (sso, sig) => {
        return this.http
            .get(`${this.globals.auth_base_url}account-api/smarttask-sso`, {
                responseType: 'text',
            })
            .toPromise()
            .then((token: string) => {
                return token;
            });
    };

    logout = (logout_from: string) => {
        console.log(`Logout From: ${logout_from}`);
        // this.companylistStoreService.updateSelectedCompanyId(undefined);
        let app_base_url = structuredClone(this.globals.app_base_url);
        if (app_base_url[app_base_url.length - 1] == '/') {
            //remove trailing slash
            app_base_url = app_base_url.substring(0, app_base_url.length - 1);
        }
        app_base_url = encodeURIComponent(app_base_url);

        let id_token = undefined;
        if (this.globals.auth_data != undefined && this.globals.auth_data.id_token != undefined) {
            id_token = encodeURIComponent(this.globals.auth_data.id_token);
        }
        this.cookieService.delete(this.globals.cookie_names.code_verifier, '/', this.globals.cookie_domain);
        this.cookieService.delete(this.globals.cookie_names.auth, '/', this.globals.cookie_domain);
        this.cookieService.delete(this.globals.cookie_names.user_id, '/', this.globals.cookie_domain);
        this.oneSignalService.logout();
        let event = new SharedEventModel();
        event.name = SharedEventNameEnum.after_logout;
        this.sharedService.broadcast(event);

        //Logout from Azure AD b2c server
        window.location.href = `${this.globals.identity_base_url}${this.globals.auth.tenant_id}/${this.globals.auth.signup_signin_policy}/oauth2/v2.0/logout?post_logout_redirect_uri=${this.globals.app_base_url}`;
    };

    listenForLogout = () => {
        this.sharedService.on(SharedEventNameEnum.logout, () => {
            this.logout('SharedService Event - Log out');
        });
    };

    listenForRefreshTokenExpired = () => {
        this.sharedService.on(SharedEventNameEnum.refresh_token_expired, (e: SharedEventModel) => {
            this.triggerAuthorizationFlow();
        });
    };

    private listenForGoToLoginPage = () => {
        this.sharedService.on(SharedEventNameEnum.goto_login_page, (e: SharedEventModel) => {
            console.log('Go to login page');
            this.triggerAuthorizationFlow();
        });
    };
}
